import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  RowClickedEvent,
  RowClassParams,
  ICellRendererParams,
  FirstDataRenderedEvent,
  GridSizeChangedEvent,
} from 'ag-grid-community';
import { SearchService } from '../search.service';
import { SearchResult } from '../search.model';
import { LoggingService } from '../../../core/services/logging.service';
import { HlmIconComponent, HlmProgressComponent } from '../../../ui';

const CATEGORY_CONFIG: Record<string, { icon: string; color: string }> = {
  // Deep ink-blue primary for customers, teal/green for services, amber for users —
  // muted variants that sit on the warm parchment background without screaming.
  customer:              { icon: '🏢', color: 'oklch(0.42 0.15 268)' },
  phoneservice:          { icon: '📞', color: 'oklch(0.50 0.10 170)' },
  useraccount:           { icon: '👤', color: 'oklch(0.55 0.14 50)' },
  meetingroom:           { icon: '🚪', color: 'oklch(0.50 0.12 310)' },
  servicedoffice:        { icon: '🏛', color: 'oklch(0.50 0.12 290)' },
  businessaddressservice:{ icon: '📍', color: 'oklch(0.50 0.10 190)' },
};

function categoryCellRenderer(params: ICellRendererParams): string {
  const value = params.value ?? '';
  const typeKey = value.split(' - ')[0].toLowerCase();
  const config = CATEGORY_CONFIG[typeKey] ?? { icon: '•', color: 'oklch(0.50 0 0)' };
  return `<span style="display:inline-flex;align-items:center;gap:8px;">
    <span style="font-size:12px;color:${config.color};">${config.icon}</span>
    <span>${value}</span>
  </span>`;
}

@Component({
  selector: 'cmt-search-results',
  standalone: true,
  imports: [AsyncPipe, DecimalPipe, AgGridAngular, HlmIconComponent, HlmProgressComponent],
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
})
export class SearchResultsComponent {
  private searchService = inject(SearchService);
  private router = inject(Router);
  private readonly loggingService = inject(LoggingService);

  results$ = this.searchService.searchResults$;
  loading$ = this.searchService.isLoading$;
  totalHits$ = this.searchService.totalResults$;
  hasSearched$ = this.searchService.hasSearched$;

  columnDefs: ColDef[] = [
    { field: 'brand', headerName: 'Brand', minWidth: 80, maxWidth: 110 },
    { field: 'customerId', headerName: 'Customer ID', minWidth: 75, maxWidth: 90 },
    { field: 'companyName', headerName: 'Company', minWidth: 130, flex: 2 },
    { field: 'firstName', headerName: 'First name', minWidth: 90, flex: 1 },
    { field: 'lastName', headerName: 'Last name', minWidth: 90, flex: 1 },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 190,
      flex: 1.5,
      cellRenderer: categoryCellRenderer,
    },
    { field: 'detail', headerName: 'Detail', minWidth: 140, flex: 1 },
    { field: 'externId', headerName: 'Extern ID', minWidth: 80, maxWidth: 110 },
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  /** Row color coding based on status from Elasticsearch. Styles in component SCSS. */
  getRowClass(params: RowClassParams): string {
    return params.data?.status ?? '';
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.sizeColumnsToFit();
  }

  onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }

  onRowClicked(event: RowClickedEvent): void {
    const row = event.data as SearchResult;
    if (!row?.customerId) return;

    switch (row.entityType) {
      case 'useraccount':
        this.emitSelect('ua');
        this.router.navigate(['/', row.customerId, 'ua', row.entityId, 'account-details']);
        break;
      case 'phoneservice':
        this.emitSelect('ps');
        this.router.navigate(['/', row.customerId, 'ps', row.entityId, 'settings']);
        break;
      default:
        this.emitSelect('customer');
        this.router.navigate(['/', row.customerId, 'information']);
    }
  }

  /**
   * @ticket EB-3798
   * Usage: a classic (AG-Grid) search result was chosen and navigation follows.
   */
  private emitSelect(resultType: 'customer' | 'ua' | 'ps'): void {
    this.loggingService.usage('search.select', { resultType });
  }
}
